import type { CuotaResponse } from "../schemas/mortgage";

/**
 * Determines the "current" cuota to display from a cronograma.
 *
 * Rules:
 * 1. Find the first **regular** cuota (skipping `es_amortizacion_extra` rows) whose `fecha_vencimiento` >= today.
 * 2. If all regular cuotas are in the past, return the last regular cuota.
 * 3. If the cronograma is empty or has no regular cuotas, return `null`.
 *
 * @param cronograma - Array of cuotas from the API response
 * @param today      - Reference date (defaults to today). Pass a fixed date in tests.
 * @returns The matching `CuotaResponse`, or `null` if nothing qualifies.
 */
export function getCurrentCuota(
	cronograma: CuotaResponse[],
	today: Date = new Date(),
): CuotaResponse | null {
	// Normalise to midnight so time-of-day doesn't affect the comparison
	const todayMidnight = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate(),
	);

	const regularCuotas = cronograma.filter(
		(c) => !c.es_amortizacion_extra,
	);

	if (regularCuotas.length === 0) return null;

	const upcoming = regularCuotas.find((c) => {
		const due = new Date(c.fecha_vencimiento + "T00:00:00");
		return due >= todayMidnight;
	});

	return upcoming ?? regularCuotas[regularCuotas.length - 1];
}

/**
 * Calculates the split between interest and principal for a single mortgage payment.
 * Uses the balance at the beginning of the period.
 *
 * @param balance - Current loan balance
 * @param annualRate - Annual interest rate (e.g., 7.5 for 7.5%)
 * @param totalMonthlyPayment - Total cuota (excluding insurance/fees, just P+I)
 * @returns Object with interest and principal amounts
 */
export function getMortgageSplit(
	balance: number,
	annualRate: number,
	totalMonthlyPayment: number,
): { interest: number; principal: number } {
	const monthlyRate = annualRate / 100 / 12;
	const interest = balance * monthlyRate;
	const principal = totalMonthlyPayment - interest;

	return {
		interest,
		principal: Math.max(0, principal),
	};
}

/**
 * Bono del Buen Pagador ranges and amounts by year
 * Each year has price ranges with corresponding traditional and green bonus amounts
 */
interface BonoRange {
	minPrice: number;
	maxPrice: number;
	traditional: number;
	green: number;
}

const BONO_RANGES_BY_YEAR: Record<number, BonoRange[]> = {
	2021: [
		{ minPrice: 61200, maxPrice: 87400, traditional: 24600, green: 29700 },
		{ minPrice: 87400, maxPrice: 130900, traditional: 20500, green: 25600 },
		{ minPrice: 130900, maxPrice: 218100, traditional: 18800, green: 23900 },
		{ minPrice: 218100, maxPrice: 323100, traditional: 10300, green: 15400 },
	],
	2022: [
		{ minPrice: 65200, maxPrice: 93100, traditional: 25700, green: 31100 },
		{ minPrice: 93100, maxPrice: 139400, traditional: 21400, green: 26800 },
		{ minPrice: 139400, maxPrice: 232200, traditional: 19600, green: 25000 },
		{ minPrice: 232200, maxPrice: 343900, traditional: 7300, green: 12700 },
	],
	2023: [
		{ minPrice: 65200, maxPrice: 93100, traditional: 25700, green: 31100 },
		{ minPrice: 93100, maxPrice: 139400, traditional: 21400, green: 26800 },
		{ minPrice: 139400, maxPrice: 232200, traditional: 19600, green: 25000 },
		{ minPrice: 232200, maxPrice: 343900, traditional: 10800, green: 16200 },
	],
	2024: [
		{ minPrice: 67400, maxPrice: 96200, traditional: 26900, green: 32900 },
		{ minPrice: 96200, maxPrice: 144000, traditional: 22400, green: 28400 },
		{ minPrice: 144000, maxPrice: 239800, traditional: 20500, green: 26500 },
		{ minPrice: 239800, maxPrice: 355100, traditional: 7600, green: 13600 },
	],
	2025: [
		{ minPrice: 68800, maxPrice: 98100, traditional: 27400, green: 33700 },
		{ minPrice: 98100, maxPrice: 146900, traditional: 22800, green: 29100 },
		{ minPrice: 146900, maxPrice: 244600, traditional: 20900, green: 27200 },
		{ minPrice: 244600, maxPrice: 362100, traditional: 7800, green: 14100 },
	],
	// 2026: D.S. N.° 011-2026-VIVIENDA.
	// `traditional` (BBP): valores oficiales publicados en El Peruano.
	// `green` (Bono Verde = BBP + Bono Sostenible): la norma publicó solo el BBP;
	// la tabla del Bono Sostenible 2026 vive en el anexo del decreto. Aquí está
	// DERIVADO manteniendo el patrón histórico (delta plano sobre el BBP, que fue
	// +6300 en 2025) ajustado por el mismo IPC de los rangos (×1.0153) => +6400.
	// Confirmar contra el anexo oficial del D.S. 011-2026-VIVIENDA y reemplazar.
	2026: [
		{ minPrice: 69900, maxPrice: 99600, traditional: 27800, green: 34200 },
		{ minPrice: 99600, maxPrice: 149200, traditional: 23200, green: 29600 },
		{ minPrice: 149200, maxPrice: 248300, traditional: 21200, green: 27600 },
		{ minPrice: 248300, maxPrice: 367600, traditional: 7900, green: 14300 },
	],
};

/**
 * Get the maximum year available in the Bono lookup table
 */
function getMaxAvailableYear(): number {
	return Math.max(...Object.keys(BONO_RANGES_BY_YEAR).map(Number));
}

/**
 * Calculate Bono del Buen Pagador based on price, year, and bonus type
 *
 * @param price - Property price in PEN
 * @param year - Acquisition year (2021+)
 * @param isGreen - Whether to calculate green (sustainable) bonus
 * @returns Bonus amount in PEN
 */
export function calculateBonoMivivienda(
	price: number,
	year?: number,
	isGreen: boolean = false,
): number {
	// Default to current year if no year provided
	const currentYear = new Date().getFullYear();
	const acquisitionYear = year || currentYear;

	// For years beyond available data, use the latest available year as fallback
	const maxYear = getMaxAvailableYear();
	const lookupYear = acquisitionYear > maxYear ? maxYear : acquisitionYear;

	// Get the ranges for the specified year
	const ranges = BONO_RANGES_BY_YEAR[lookupYear];

	// If year not found (e.g., before 2021), return 0
	if (!ranges) {
		return 0;
	}

	// Find the matching price range
	const matchingRange = ranges.find(
		(range) => price >= range.minPrice && price <= range.maxPrice,
	);

	if (!matchingRange) {
		return 0;
	}

	return isGreen ? matchingRange.green : matchingRange.traditional;
}

/**
 * Get the valid price range for a given year
 *
 * @param year - Acquisition year
 * @returns Object with min and max valid prices, or null if year not found
 */
export function getValidPriceRange(
	year: number,
): { min: number; max: number } | null {
	// For years beyond available data, use the latest available year as fallback
	const maxYear = getMaxAvailableYear();
	const lookupYear = year > maxYear ? maxYear : year;

	const ranges = BONO_RANGES_BY_YEAR[lookupYear];
	if (!ranges || ranges.length === 0) {
		return null;
	}

	return {
		min: ranges[0].minPrice,
		max: ranges[ranges.length - 1].maxPrice,
	};
}

/**
 * Get the specific matching range for a given year and price
 *
 * @param year - Acquisition year
 * @param price - Property price
 * @returns Object with min and max valid prices for the specific matched tier, or null if no tier matches
 */
export function getMatchingBonoRange(
	year: number,
	price: number,
): { minPrice: number; maxPrice: number } | null {
	const currentYear = new Date().getFullYear();
	const acquisitionYear = year || currentYear;

	const maxYear = getMaxAvailableYear();
	const lookupYear = acquisitionYear > maxYear ? maxYear : acquisitionYear;

	const ranges = BONO_RANGES_BY_YEAR[lookupYear];
	if (!ranges || ranges.length === 0) {
		return null;
	}

	const matchingRange = ranges.find(
		(range) => price >= range.minPrice && price <= range.maxPrice,
	);

	return matchingRange || null;
}

export type BreakEvenAdviceStatus = "si" | "poco" | "no";

export interface BreakEvenAdvice {
	status: BreakEvenAdviceStatus;
	/** Years remaining until the break-even point. Negative if already passed. */
	yearsToBreakEven: number;
}

/** Years remaining above which amortizing is clearly worth it (strictly greater). */
const BREAK_EVEN_WORTH_IT_YEARS = 5;

/**
 * Whole months elapsed since the first payment date, relative to `today`
 * (clamped at 0). Returns `null` when the date is missing or invalid.
 *
 * @param firstPaymentDate - ISO date string (fecha_primera_cuota).
 * @param today - Reference date (defaults to now). Pass a fixed date in tests.
 */
export function getElapsedLoanMonths(
	firstPaymentDate: string | null | undefined,
	today: Date = new Date(),
): number | null {
	if (!firstPaymentDate) return null;

	const start = new Date(firstPaymentDate + "T00:00:00");
	if (Number.isNaN(start.getTime())) return null;

	const todayMidnight = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate(),
	);

	return Math.max(
		0,
		(todayMidnight.getFullYear() - start.getFullYear()) * 12 +
			(todayMidnight.getMonth() - start.getMonth()),
	);
}

/**
 * Decides whether it's still worth making extra principal payments (amortizar),
 * based on how far "today" is from the break-even point.
 *
 * The break-even month comes from the backend cronograma summary
 * (`breakeven_mes`, a 1-based month index from loan start). "Today" is derived
 * from how many whole months have elapsed since the first payment date.
 *
 * Thresholds (years remaining to break-even):
 *   > 5       -> "si"   (worth it)
 *   0 < x <= 5 -> "poco" (marginal)
 *   <= 0      -> "no"   (break-even passed)
 *
 * @param breakevenMes - Break-even month index (1-based). Nullable.
 * @param firstPaymentDate - ISO date string of the first payment (fecha_primera_cuota).
 * @param today - Reference date (defaults to now). Pass a fixed date in tests.
 * @returns Advice object, or `null` when the break-even point is unknown.
 */
export function getBreakEvenAdvice(
	breakevenMes: number | string | null | undefined,
	firstPaymentDate: string | null | undefined,
	today: Date = new Date(),
): BreakEvenAdvice | null {
	if (breakevenMes == null || breakevenMes === "") return null;
	const breakEvenMonth = Number(breakevenMes);
	if (!Number.isFinite(breakEvenMonth) || breakEvenMonth <= 0) return null;

	const elapsedMonths = getElapsedLoanMonths(firstPaymentDate, today);
	if (elapsedMonths == null) return null;

	const yearsToBreakEven = (breakEvenMonth - elapsedMonths) / 12;

	let status: BreakEvenAdviceStatus;
	if (yearsToBreakEven <= 0) {
		status = "no";
	} else if (yearsToBreakEven <= BREAK_EVEN_WORTH_IT_YEARS) {
		status = "poco";
	} else {
		status = "si";
	}

	return { status, yearsToBreakEven };
}

/**
 * Calculates duration in years and months from total months
 * @param months - Total number of months
 */
export function calculateDuration(months: number): {
	years: number;
	months: number;
} {
	return {
		years: Math.floor(months / 12),
		months: months % 12,
	};
}
